import { makeObservable, observable, action, runInAction, reaction } from 'mobx';
import { getAgentById, getAgentOverview, updateAgent } from '@/api/agents';
import { getSkillsForAgent } from '@/api/skills';
import { getA2ASkillsForAgent } from '@/api/a2aSkills';
import {
  getKnowledgeForAgent,
  linkKnowledgeToAgent,
  unlinkKnowledgeFromAgent,
} from '@/api/agentKnowledge';
import { grantPluginToAgent, revokePluginFromAgent } from '@/api/agentPlugins';
import {
  getPhraseGroupsForAgent,
  attachPhraseGroupToAgent,
  detachPhraseGroupFromAgent,
  type AgentPhraseGroupView,
} from '@/api/phraseLists';
import { getPeersForAgent, addPeer, updatePeer, removePeer, type PeerInput } from '@/api/agentConsultingPeers';
import {
  searchPromptsForAgent,
  pinPromptToAgent,
  unpinPromptFromAgent,
  updatePinDisplayOrder,
} from '@/api/prebuiltPrompts';
import type {
  AgentDefinition,
  AgentOverviewBundle,
  AiSkill,
  AiA2AAgentSkill,
  AiAgentKnowledge,
  AiAgentConsultingPeer,
  AssistantAIPrebuiltPromptLink,
} from '@/api/types';
import type { RootStore } from './RootStore';

// Holds full detail for one agent. Each tab pulls from a separate slice of
// state so loading + errors are local to a tab. All fetches kick off in
// parallel on `loadFor()` — switching agents clears previous data so the new
// page doesn't briefly render the wrong thing.
//
// The agent belongs to the practice selected when it loaded. Every load takes a
// request number; an answer (or an error) for an older number is dropped, so a
// late reply can never repaint the page for another practice or agent. When the
// selected practice changes, everything shown is cleared at once and loaded again
// for the new practice. An agent owned by a DIFFERENT practice than the one
// selected is never held as `agent` — it becomes `foreign`, nothing is writable,
// and the page says whose it is. Global agents (merchantId null) belong to every
// practice and simply reload.

/** An agent the URL names that belongs to a practice other than the selected one. */
export interface ForeignAgent {
  agentId: number;
  displayName: string;
  ownerMerchantId: number;
}

const belongsTo = (agent: AgentDefinition, merchantId: number | null): boolean =>
  agent.merchantId == null || agent.merchantId === merchantId;

export class AgentDetailStore {
  @observable agentId: number | null = null;
  @observable agent: AgentDefinition | null = null;
  @observable foreign: ForeignAgent | null = null;
  @observable overview: AgentOverviewBundle | null = null;
  @observable proceduralSkills: AiSkill[] = [];
  @observable a2aSkills: AiA2AAgentSkill[] = [];
  @observable knowledge: AiAgentKnowledge[] = [];
  @observable phraseGroups: AgentPhraseGroupView[] = [];
  @observable peers: AiAgentConsultingPeer[] = [];
  @observable pinnedPrompts: AssistantAIPrebuiltPromptLink[] = [];

  @observable isLoadingAgent = false;
  @observable isLoadingOverview = false;
  @observable isLoadingProcedural = false;
  @observable isLoadingA2A = false;
  @observable isLoadingKnowledge = false;
  @observable isLoadingPeers = false;
  @observable isLoadingPinnedPrompts = false;
  @observable isSavingSkillRouter = false;
  @observable isLoadingPhraseGroups = false;
  @observable isSavingPhraseList = false;

  @observable agentError: string | null = null;
  @observable overviewError: string | null = null;
  @observable proceduralError: string | null = null;
  @observable a2aError: string | null = null;
  @observable knowledgeError: string | null = null;
  @observable phraseGroupsError: string | null = null;
  @observable peersError: string | null = null;
  @observable pinnedPromptsError: string | null = null;

  /** Bumps on every load, practice change and clear; an answer carrying an older number is ignored. */
  private seq = 0;

  constructor(private root: RootStore) {
    makeObservable(this);
    // A practice switch is a context transition, not a relabel: reload the open agent for the new practice.
    reaction(
      () => this.root.merchantContextStore.selectedMerchantId,
      () => this.practiceChanged(),
    );
  }

  @action
  async loadFor(agentId: number): Promise<void> {
    if (this.agentId !== agentId) this.wipe();
    this.agentId = agentId;
    this.foreign = null;
    const seq = ++this.seq;
    void this.fetchAgent(agentId, seq);
    void this.fetchOverview(agentId, seq);
    void this.fetchProceduralSkills(agentId, seq);
    void this.fetchA2ASkills(agentId, seq);
    void this.fetchKnowledge(agentId, seq);
    void this.fetchPhraseGroups(agentId, seq);
    void this.fetchPeers(agentId, seq);
    void this.fetchPinnedPrompts(agentId, seq);
  }

  /** The selected practice changed under an open agent: nothing loaded for the old one stays on screen or writable. */
  @action
  private practiceChanged(): void {
    if (this.agentId == null) return;
    const agentId = this.agentId;
    this.wipe();
    void this.loadFor(agentId);
  }

  /** The agent named by the URL is another practice's: hold only who owns it, drop everything else in flight. */
  @action
  private showForeign(agent: AgentDefinition): void {
    this.seq++;
    this.wipe();
    this.foreign = { agentId: agent.id, displayName: agent.displayName, ownerMerchantId: agent.merchantId! };
  }

  /** Who a write may target: the agent loaded under the practice selected now, with the load it belongs to. */
  private target(): { agentId: number; seq: number } | null {
    return this.agent && this.agent.id === this.agentId ? { agentId: this.agent.id, seq: this.seq } : null;
  }

  @action
  private async fetchAgent(agentId: number, seq = this.seq): Promise<void> {
    this.isLoadingAgent = true;
    this.agentError = null;
    try {
      const agent = await getAgentById(agentId);
      runInAction(() => {
        if (seq !== this.seq) return;
        this.isLoadingAgent = false;
        if (!belongsTo(agent, this.root.merchantContextStore.selectedMerchantId)) {
          this.showForeign(agent);
          return;
        }
        this.agent = agent;
        // Push to the per-merchant Recent list — drives the "🕒 Recent" section
        // on the Agents page. localStorage; instant, no network. Only once the
        // agent is known to belong to the practice whose list it joins.
        this.root.agentRegistryStore.trackOpened(agentId);
      });
    } catch (err) {
      runInAction(() => {
        if (seq !== this.seq) return;
        this.isLoadingAgent = false;
        this.agentError = err instanceof Error ? err.message : 'Failed to load agent';
      });
    }
  }

  @action
  private async fetchOverview(agentId: number, seq = this.seq): Promise<void> {
    this.isLoadingOverview = true;
    this.overviewError = null;
    try {
      const ov = await getAgentOverview(agentId);
      runInAction(() => {
        if (seq !== this.seq) return;
        this.overview = ov;
        this.isLoadingOverview = false;
      });
    } catch (err) {
      runInAction(() => {
        if (seq !== this.seq) return;
        this.isLoadingOverview = false;
        this.overviewError = err instanceof Error ? err.message : 'Failed to load overview';
      });
    }
  }

  /**
   * Replace the in-memory agent record after a successful PATCH-like
   * write (e.g. AgentCategoryEditor swapping CategoryId via updateAgent).
   * The API returns the full saved entity; we just trust it. Safe even if
   * the user has clicked into a different agent in the meantime — we
   * gate by `agentId` to avoid stomping the wrong page's state.
   *
   * Also nudges the registry store so the agents list reflects the
   * change without a full refetch.
   */
  @action
  applyAgentPatch(next: AgentDefinition): void {
    if (this.agent && this.agent.id === next.id) {
      this.agent = next;
    }
    // Patch the registry list in-place. The list is fetched lazily and
    // a full refetch would feel sluggish for a single field change.
    const reg = this.root.agentRegistryStore;
    const idx = reg.agents.findIndex((a) => a.id === next.id);
    if (idx >= 0) {
      runInAction(() => {
        reg.agents[idx] = { ...reg.agents[idx], ...next };
      });
    }
  }

  @action
  private async fetchProceduralSkills(agentId: number, seq = this.seq): Promise<void> {
    this.isLoadingProcedural = true;
    this.proceduralError = null;
    try {
      const skills = await getSkillsForAgent(agentId);
      runInAction(() => {
        if (seq !== this.seq) return;
        this.proceduralSkills = skills;
        this.isLoadingProcedural = false;
      });
    } catch (err) {
      runInAction(() => {
        if (seq !== this.seq) return;
        this.isLoadingProcedural = false;
        this.proceduralError = err instanceof Error ? err.message : 'Failed to load procedures';
      });
    }
  }

  @action
  private async fetchA2ASkills(agentId: number, seq = this.seq): Promise<void> {
    this.isLoadingA2A = true;
    this.a2aError = null;
    try {
      const skills = await getA2ASkillsForAgent(agentId);
      runInAction(() => {
        if (seq !== this.seq) return;
        this.a2aSkills = skills;
        this.isLoadingA2A = false;
      });
    } catch (err) {
      runInAction(() => {
        if (seq !== this.seq) return;
        this.isLoadingA2A = false;
        this.a2aError = err instanceof Error ? err.message : 'Failed to load A2A skills';
      });
    }
  }

  @action
  private async fetchKnowledge(agentId: number, seq = this.seq): Promise<void> {
    this.isLoadingKnowledge = true;
    this.knowledgeError = null;
    try {
      const items = await getKnowledgeForAgent(agentId);
      runInAction(() => {
        if (seq !== this.seq) return;
        this.knowledge = items;
        this.isLoadingKnowledge = false;
      });
    } catch (err) {
      runInAction(() => {
        if (seq !== this.seq) return;
        this.isLoadingKnowledge = false;
        this.knowledgeError = err instanceof Error ? err.message : 'Failed to load knowledge';
      });
    }
  }

  /**
   * Link a Knowledge base to the current agent. Refetches the knowledge list
   * after the API call so the new join row (with its server-issued Id and
   * the nested Knowledge object's source counts) appears in the table. The
   * backend rejects duplicate (agentId, knowledgeId) pairs with isSuccess=false,
   * which our api wrapper surfaces as a thrown Error — the calling UI catches
   * and shows the message in plain language.
   */
  @action
  async linkKnowledge(knowledgeId: number): Promise<void> {
    const t = this.target();
    if (!t) return;
    await linkKnowledgeToAgent(t.agentId, knowledgeId);
    await this.fetchKnowledge(t.agentId, t.seq);
  }

  /**
   * Unlink a Knowledge base from the current agent. Optimistically removes
   * the row from the table for instant feedback; on failure, refetches to
   * reconcile and surfaces the error message to the caller.
   */
  @action
  async unlinkKnowledge(linkId: number): Promise<void> {
    const t = this.target();
    if (!t) return;
    const previous = this.knowledge;
    runInAction(() => {
      this.knowledge = this.knowledge.filter((k) => k.id !== linkId);
    });
    try {
      await unlinkKnowledgeFromAgent(linkId);
    } catch (err) {
      // Restore the row so the user's table reflects reality, then re-throw
      // so the UI can show the error.
      runInAction(() => { if (t.seq === this.seq) this.knowledge = previous; });
      throw err;
    }
  }

  // ── Tools (grant/revoke plugins on this agent) ──────────────────────────
  // Writes ai.AgentPlugin — the table the runtime resolves the model's tool
  // list from. The Tools tab reads overview.plugins, so both grant and revoke
  // refetch the overview to reflect the new set (which carries each plugin's
  // full row: name, description, category, global reach). Grant/revoke are
  // idempotent server-side.

  @action
  async grantPlugin(pluginName: string): Promise<void> {
    const t = this.target();
    if (!t) return;
    await grantPluginToAgent(t.agentId, pluginName);
    await this.fetchOverview(t.agentId, t.seq);
  }

  @action
  async revokePlugin(pluginName: string): Promise<void> {
    const t = this.target();
    if (!t) return;
    const agentId = t.agentId;
    // Optimistically drop the tool from the tab for instant feedback; the
    // overview refetch below reconciles, and a failure restores via refetch too.
    const previous = this.overview;
    if (this.overview?.plugins) {
      runInAction(() => {
        if (this.overview) {
          this.overview = {
            ...this.overview,
            plugins: this.overview.plugins?.filter((p) => p.name !== pluginName),
          };
        }
      });
    }
    try {
      await revokePluginFromAgent(agentId, pluginName);
      await this.fetchOverview(agentId, t.seq);
    } catch (err) {
      runInAction(() => { if (t.seq === this.seq) this.overview = previous; });
      throw err;
    }
  }

  // ── Phrase-list buckets (assign/unassign to this agent) ─────────────────

  @action
  private async fetchPhraseGroups(agentId: number, seq = this.seq): Promise<void> {
    this.isLoadingPhraseGroups = true;
    this.phraseGroupsError = null;
    try {
      const items = await getPhraseGroupsForAgent(agentId);
      runInAction(() => {
        if (seq !== this.seq) return;
        this.phraseGroups = items;
        this.isLoadingPhraseGroups = false;
      });
    } catch (err) {
      runInAction(() => {
        if (seq !== this.seq) return;
        this.isLoadingPhraseGroups = false;
        this.phraseGroupsError = err instanceof Error ? err.message : 'Failed to load phrase buckets';
      });
    }
  }

  /** Attach a phrase bucket to the current agent; refetch so the new link row appears. */
  @action
  async attachPhraseGroup(groupId: number): Promise<void> {
    const t = this.target();
    if (!t) return;
    await attachPhraseGroupToAgent(t.agentId, groupId);
    await this.fetchPhraseGroups(t.agentId, t.seq);
  }

  /** Detach a phrase bucket by link id; optimistic remove, revert on failure. */
  @action
  async detachPhraseGroup(linkId: number): Promise<void> {
    const t = this.target();
    if (!t) return;
    const previous = this.phraseGroups;
    runInAction(() => {
      this.phraseGroups = this.phraseGroups.filter((g) => g.linkId !== linkId);
    });
    try {
      await detachPhraseGroupFromAgent(linkId);
    } catch (err) {
      runInAction(() => { if (t.seq === this.seq) this.phraseGroups = previous; });
      throw err;
    }
  }

  /** Public refresh hook (used after attaching from the drawer). */
  @action
  async refreshPhraseGroups(): Promise<void> {
    const t = this.target();
    if (t) await this.fetchPhraseGroups(t.agentId, t.seq);
  }

  @action
  private async fetchPeers(agentId: number, seq = this.seq): Promise<void> {
    this.isLoadingPeers = true;
    this.peersError = null;
    try {
      const items = await getPeersForAgent(agentId);
      runInAction(() => {
        if (seq !== this.seq) return;
        this.peers = items;
        this.isLoadingPeers = false;
      });
    } catch (err) {
      runInAction(() => {
        if (seq !== this.seq) return;
        this.isLoadingPeers = false;
        this.peersError = err instanceof Error ? err.message : 'Failed to load peers';
      });
    }
  }

  @action
  refresh(): void {
    if (this.agentId != null) void this.loadFor(this.agentId);
  }

  // ── Peer mutations ─────────────────────────────────────────────────────

  @action
  async addPeer(input: Omit<PeerInput, 'primaryAgentId'>): Promise<void> {
    const t = this.target();
    if (!t) throw new Error('No agent loaded');
    await addPeer({ ...input, primaryAgentId: t.agentId });
    await this.fetchPeers(t.agentId, t.seq);
  }

  @action
  async updatePeer(input: Omit<PeerInput, 'primaryAgentId'>): Promise<void> {
    const t = this.target();
    if (!t) throw new Error('No agent loaded');
    await updatePeer({ ...input, primaryAgentId: t.agentId });
    await this.fetchPeers(t.agentId, t.seq);
  }

  @action
  async removePeer(linkId: number): Promise<void> {
    const t = this.target();
    if (!t) throw new Error('No agent loaded');
    await removePeer(linkId);
    runInAction(() => {
      if (t.seq === this.seq) this.peers = this.peers.filter((p) => p.id !== linkId);
    });
  }

  // Promote private fetcher to public so the modal can refresh on open.
  @action
  async refreshPeers(): Promise<void> {
    const t = this.target();
    if (t) await this.fetchPeers(t.agentId, t.seq);
  }

  @observable isSavingAudience = false;

  // Optimistic toggle for one audience flag (patientApp or merchantApp). The
  // backend Update endpoint expects the full AgentDefinition, so we send the
  // loaded record with the relevant flag flipped. Revert + rethrow on failure.
  // Mirrors setEnableSkillRouter — same optimistic pattern, same loud-failure
  // contract — so the AudienceTile UI can render with consistent feedback.
  @action
  async setAudienceFlag(field: 'patientApp' | 'merchantApp', enabled: boolean): Promise<void> {
    const t = this.target();
    if (!t || !this.agent) throw new Error('No agent loaded');
    const previous = (this.agent[field] as boolean | undefined) ?? false;
    if (previous === enabled) return;

    runInAction(() => {
      if (this.agent) this.agent = { ...this.agent, [field]: enabled };
      this.isSavingAudience = true;
    });

    try {
      const next: AgentDefinition = { ...this.agent, [field]: enabled };
      const saved = await updateAgent(next);
      runInAction(() => {
        if (t.seq === this.seq) this.agent = saved;
      });
    } catch (e) {
      runInAction(() => {
        if (t.seq === this.seq && this.agent) this.agent = { ...this.agent, [field]: previous };
      });
      throw e;
    } finally {
      runInAction(() => {
        this.isSavingAudience = false;
      });
    }
  }

  // Optimistic toggle for the per-agent skill router. The backend Update
  // endpoint expects the full AgentDefinition, so we send the loaded record
  // with `enableSkillRouter` flipped. Revert + rethrow on failure so the UI
  // can show a message.
  @action
  async setEnableSkillRouter(enabled: boolean): Promise<void> {
    const t = this.target();
    if (!t || !this.agent) throw new Error('No agent loaded');
    const previous = this.agent.enableSkillRouter ?? false;
    if (previous === enabled) return;

    runInAction(() => {
      if (this.agent) this.agent = { ...this.agent, enableSkillRouter: enabled };
      this.isSavingSkillRouter = true;
    });

    try {
      const next: AgentDefinition = { ...this.agent, enableSkillRouter: enabled };
      const saved = await updateAgent(next);
      runInAction(() => {
        if (t.seq === this.seq) this.agent = saved;
      });
    } catch (e) {
      runInAction(() => {
        if (t.seq === this.seq && this.agent) this.agent = { ...this.agent, enableSkillRouter: previous };
      });
      throw e;
    } finally {
      runInAction(() => {
        this.isSavingSkillRouter = false;
      });
    }
  }

  // Optimistic toggle for the per-agent phrase-list master switch. Mirrors
  // setEnableSkillRouter — sends the full AgentDefinition with `enablePhraseList`
  // flipped; revert + rethrow on failure so the tile can show a message.
  @action
  async setEnablePhraseList(enabled: boolean): Promise<void> {
    const t = this.target();
    if (!t || !this.agent) throw new Error('No agent loaded');
    const previous = this.agent.enablePhraseList ?? false;
    if (previous === enabled) return;

    runInAction(() => {
      if (this.agent) this.agent = { ...this.agent, enablePhraseList: enabled };
      this.isSavingPhraseList = true;
    });

    try {
      const next: AgentDefinition = { ...this.agent, enablePhraseList: enabled };
      const saved = await updateAgent(next);
      runInAction(() => {
        if (t.seq === this.seq) this.agent = saved;
      });
    } catch (e) {
      runInAction(() => {
        if (t.seq === this.seq && this.agent) this.agent = { ...this.agent, enablePhraseList: previous };
      });
      throw e;
    } finally {
      runInAction(() => {
        this.isSavingPhraseList = false;
      });
    }
  }

  // ── Pinned prebuilt prompts ────────────────────────────────────────────

  @action
  private async fetchPinnedPrompts(agentId: number, seq = this.seq): Promise<void> {
    this.isLoadingPinnedPrompts = true;
    this.pinnedPromptsError = null;
    try {
      const items = await searchPromptsForAgent(agentId);
      runInAction(() => {
        if (seq !== this.seq) return;
        this.pinnedPrompts = items;
        this.isLoadingPinnedPrompts = false;
      });
    } catch (err) {
      runInAction(() => {
        if (seq !== this.seq) return;
        this.isLoadingPinnedPrompts = false;
        this.pinnedPromptsError =
          err instanceof Error ? err.message : 'Failed to load pinned prompts';
      });
    }
  }

  @action
  async refreshPinnedPrompts(): Promise<void> {
    const t = this.target();
    if (t) await this.fetchPinnedPrompts(t.agentId, t.seq);
  }

  @action
  async pinPrompt(promptId: number, displayOrder?: number): Promise<void> {
    const t = this.target();
    if (!t) throw new Error('No agent loaded');
    const order = displayOrder ?? this.pinnedPrompts.length;
    await pinPromptToAgent({
      assistantAIId: t.agentId,
      prebuiltPromptId: promptId,
      displayOrder: order,
    });
    await this.fetchPinnedPrompts(t.agentId, t.seq);
  }

  @action
  async unpinPrompt(linkRowId: number): Promise<void> {
    const t = this.target();
    if (!t) throw new Error('No agent loaded');
    await unpinPromptFromAgent(linkRowId);
    runInAction(() => {
      if (t.seq === this.seq) this.pinnedPrompts = this.pinnedPrompts.filter((p) => p.id !== linkRowId);
    });
  }

  @action
  async reorderPinnedPrompt(linkRowId: number, newOrder: number): Promise<void> {
    const t = this.target();
    if (!t) throw new Error('No agent loaded');
    await updatePinDisplayOrder(linkRowId, newOrder);
    await this.fetchPinnedPrompts(t.agentId, t.seq);
  }

  /** Leaves the agent: every answer still in flight is dropped. */
  @action
  clear(): void {
    this.seq++;
    this.wipe();
    this.agentId = null;
    this.foreign = null;
  }

  /** Empties every slice, error and busy flag, so nothing of the previous agent or practice can show. */
  @action
  private wipe(): void {
    this.agent = null;
    this.overview = null;
    this.proceduralSkills = [];
    this.a2aSkills = [];
    this.knowledge = [];
    this.phraseGroups = [];
    this.peers = [];
    this.pinnedPrompts = [];
    this.isLoadingAgent = this.isLoadingOverview = this.isLoadingProcedural = this.isLoadingA2A = false;
    this.isLoadingKnowledge = this.isLoadingPeers = this.isLoadingPinnedPrompts = this.isLoadingPhraseGroups = false;
    this.agentError = this.overviewError = this.proceduralError = this.a2aError = null;
    this.knowledgeError = this.phraseGroupsError = this.peersError = this.pinnedPromptsError = null;
  }
}
